import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { SocialGraph } from './SocialGraph';
import styles from './SocialGraphPOC.module.scss';

interface GraphViewOverlayProps {
  graphId: number;
  onClose: () => void;
}

// Full-viewport overlay hosting the graph view, opened from a source page
// (case / topic event / actor group) so closing it returns the user to exactly
// where they were — same page, tab, and scroll position. Coming from the Graphs
// list still uses the real /graphs/:graphId route instead.
export function GraphViewOverlay({ graphId, onClose }: GraphViewOverlayProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  // dialog housekeeping: focus in on open + restore to the opener on close,
  // lock body scroll, and make the app behind inert (real focus containment —
  // the overlay is portaled to <body>, outside #root, so it stays interactive)
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const appRoot = document.getElementById('root');
    const previousOverflow = document.body.style.overflow;
    appRoot?.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';
    rootRef.current?.focus();
    return () => {
      appRoot?.removeAttribute('inert');
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, []);

  // Escape closes the overlay — but only when nothing inside consumed it first
  // (export/actor menus close, a pinned node unpins, a modal closes; each of
  // those calls preventDefault). The check is deferred a tick because listener
  // registration order is arbitrary: after the dispatch completes,
  // defaultPrevented reflects every listener's verdict.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setTimeout(() => {
        if (!event.defaultPrevented) onClose();
      }, 0);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      ref={rootRef}
      className={styles.graphOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('Social graph')}
      tabIndex={-1}
    >
      <SocialGraph graphId={graphId} onExit={onClose} />
    </div>,
    document.body,
  );
}
